// E8SportsDayTablePercentOpenTask.js
// Zadanie otwarte E8 (2026 zad. 17) - tabela, roznica dziewczat i chlopcow, procent.

import React from "react";
import { BlockMath, InlineMath } from "../../../../components/math/KatexWrappers";
import EquationInput from "../../../../components/inputs/EquationInput";
import FractionInput from "../../../../components/inputs/FractionInput";
import { scoreStaged } from "../../../openTaskScoring";

function rawStr(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v.raw === "string") return v.raw;
  if (typeof v.value === "string") return v.value;
  return "";
}

const BLUEPRINTS = [
  { girlsFootball: 15, girlsDance: 65, boysFootball: 46, boysDance: 16, boysTable: 34, diff: 8 },
  { girlsFootball: 18, girlsDance: 57, boysFootball: 40, boysDance: 18, boysTable: 32, diff: 10 },
  { girlsFootball: 20, girlsDance: 57, boysFootball: 42, boysDance: 21, boysTable: 32, diff: 10 },
  { girlsFootball: 22, girlsDance: 41, boysFootball: 36, boysDance: 18, boysTable: 32, diff: 8 },
  { girlsFootball: 18, girlsDance: 54, boysFootball: 44, boysDance: 20, boysTable: 34, diff: 4 },
  { girlsFootball: 28, girlsDance: 60, boysFootball: 42, boysDance: 20, boysTable: 32, diff: 12 },
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function buildData(values) {
  const boysTotal = values.boysFootball + values.boysDance + values.boysTable;
  const girlsTotal = boysTotal + values.diff;
  const girlsTable = girlsTotal - values.girlsFootball - values.girlsDance;
  const total = boysTotal + girlsTotal;
  const tableTennisTotal = girlsTable + values.boysTable;
  const percent = (100 * tableTennisTotal) / total;
  return { ...values, boysTotal, girlsTotal, girlsTable, total, tableTennisTotal, percent, maxPoints: 3 };
}

function formatPercent(value) {
  if (Math.abs(value - Math.round(value)) < 1e-9) {
    return String(Math.round(value));
  }
  return String(Number(value.toFixed(2))).replace(".", ",");
}

function createOriginalTask() {
  return {
    type: "E8SportsDayTablePercentOpenTask",
    data: buildData(BLUEPRINTS[0]),
    source: { variant: "original", exam: "E8", session: "2026_MAJ", taskNumber: 17 },
    tags: ["e8", "statystyka", "tabela", "procenty", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8SportsDayTablePercentOpenTask",
    data: buildData(pick(BLUEPRINTS.slice(1))),
    source: { variant: "random", basedOn: "E8_2026_MAJ_17" },
    tags: ["e8", "statystyka", "tabela", "procenty", "zadanie-otwarte"],
  };
}

function normalize(text) {
  return rawStr(text).toLowerCase().replace(/,/g, ".").replace(/\s+/g, "");
}

function parseNumber(text) {
  const match = normalize(text).match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
}

function extractNumbers(text) {
  return Array.from(normalize(text).matchAll(/-?\d+(?:\.\d+)?/g), (match) =>
    Number(match[0])
  ).filter(Number.isFinite);
}

function hasNumber(numbers, expected, tolerance = 0.05) {
  return numbers.some((value) => Math.abs(value - expected) <= tolerance);
}

function Table({ data }) {
  const cell = { border: "1px solid currentColor", padding: "8px 10px", textAlign: "center", opacity: 0.9 };
  return (
    <table style={{ borderCollapse: "collapse", margin: "14px auto", minWidth: 460 }}>
      <thead>
        <tr><th style={cell}></th><th style={cell}>piłka nożna</th><th style={cell}>taniec</th><th style={cell}>tenis stołowy</th></tr>
      </thead>
      <tbody>
        <tr><td style={cell}>Liczba dziewcząt</td><td style={cell}>{data.girlsFootball}</td><td style={cell}>{data.girlsDance}</td><td style={cell}>?</td></tr>
        <tr><td style={cell}>Liczba chłopców</td><td style={cell}>{data.boysFootball}</td><td style={cell}>{data.boysDance}</td><td style={cell}>{data.boysTable}</td></tr>
      </tbody>
    </table>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>Podczas Dnia Sportu w pewnej szkole rozegrano trzy turnieje sportowe. Każde dziecko wzięło udział w dokładnie jednym turnieju. W tabeli podano informacje dotyczące liczby dzieci w poszczególnych turniejach. Łączna liczba dziewcząt była o <InlineMath math={`${data.diff}`} /> większa od łącznej liczby chłopców.</p>
      <Table data={data} />
      <p>Oblicz, ile procent liczby wszystkich uczestników Dnia Sportu stanowi liczba dzieci, które brały udział w turnieju tenisa stołowego. Zapisz obliczenia.</p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const ua = userAnswer || {};
  const set = (field) => (val) => onChange?.({ ...ua, [field]: val });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div style={{ fontSize: 12.5, opacity: 0.78, lineHeight: 1.45, borderLeft: "3px solid var(--color-accent)", paddingLeft: 8 }}>
        <strong>{"Wyniki po\u015brednie s\u0105 opcjonalne."}</strong>{" "}
        {"Wpisz je, je\u015bli chcesz dosta\u0107 punkty cz\u0105stkowe. Poprawny procent uczestnik\u00f3w tenisa sto\u0142owego daje pe\u0142ne 3 punkty."}
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Liczba wszystkich chłopców:</div>
        <EquationInput value={ua.boysTotal || ""} onChange={set("boysTotal")} disabled={disabled} placeholder="np. 50 + 20 + 30 = 100" basis="340px" />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Liczba wszystkich dziewcząt:</div>
        <EquationInput value={ua.girlsTotal || ""} onChange={set("girlsTotal")} disabled={disabled} placeholder="np. 100 + 6 = 106" basis="340px" />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Liczba dzieci grających w tenisa stołowego:</div>
        <EquationInput value={ua.tennis || ""} onChange={set("tennis")} disabled={disabled} placeholder="np. 26 + 30 = 56" basis="340px" />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Procent uczestników tenisa stołowego:</div>
        <FractionInput value={ua.percent || ""} onChange={set("percent")} disabled={disabled} label="" suffixLatex="\%" placeholder="np. 25" />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const boysOk = hasNumber(extractNumbers(userAnswer.boysTotal), data.boysTotal);
  const girlsOk = hasNumber(extractNumbers(userAnswer.girlsTotal), data.girlsTotal);
  const participantsOk = boysOk && girlsOk;
  const tennisOk = hasNumber(extractNumbers(userAnswer.tennis), data.tableTennisTotal);
  const percent = parseNumber(userAnswer.percent);
  const resultOk = percent != null && Math.abs(percent - data.percent) < 0.05;

  // Wynik-first: poprawny procent => komplet (tolerancja literowek w obliczeniach).
  return scoreStaged({
    resultOk,
    milestones: [participantsOk, tennisOk],
    maxScore: data.maxPoints,
    parts: {
      "liczby uczestników": participantsOk,
      "liczba grających w tenisa": tennisOk,
      "procent": resultOk,
    },
  });
}

export function renderAnswer({ data }) {
  const percentText = formatPercent(data.percent);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.65 }}>
      <BlockMath
        math={`\\frac{${data.tableTennisTotal}}{${data.total}}\\cdot 100\\%=${percentText}\\%`}
      />
      <div>
        {"Odpowied\u017a: dzieci z turnieju tenisa sto\u0142owego stanowi\u0142y "}
        <InlineMath math={`${percentText}\\%`} />{" wszystkich uczestnik\u00f3w."}
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const percent = rawStr(userAnswer.percent);
  const boys = rawStr(userAnswer.boysTotal);
  const girls = rawStr(userAnswer.girlsTotal);
  const tennis = rawStr(userAnswer.tennis);
  if (!percent && !boys && !girls && !tennis) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ textAlign: "left", display: "grid", gap: 4 }}>
      <div>{"Liczba wszystkich ch\u0142opc\u00f3w: "}{boys || <i>(brak)</i>}</div>
      <div>{"Liczba wszystkich dziewcz\u0105t: "}{girls || <i>(brak)</i>}</div>
      <div>{"Liczba graj\u0105cych w tenisa sto\u0142owego: "}{tennis || <i>(brak)</i>}</div>
      <div>{"Procent uczestnik\u00f3w tenisa sto\u0142owego: "}<InlineMath math={`${percent || "--"}\\%`} /></div>
    </div>
  );
}
export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Obliczamy łączną liczbę chłopców.</p>
      <BlockMath math={`${data.boysFootball}+${data.boysDance}+${data.boysTable}=${data.boysTotal}`} />
      <p><strong>Krok 2.</strong> Dziewcząt było o <InlineMath math={`${data.diff}`} /> więcej.</p>
      <BlockMath math={`${data.boysTotal}+${data.diff}=${data.girlsTotal}`} />
      <p><strong>Krok 3.</strong> Obliczamy liczbę dziewcząt w turnieju tenisa stołowego i wszystkich uczestników tego turnieju.</p>
      <BlockMath math={`${data.girlsTotal}-${data.girlsFootball}-${data.girlsDance}=${data.girlsTable}`} />
      <BlockMath math={`${data.girlsTable}+${data.boysTable}=${data.tableTennisTotal}`} />
      <p><strong>Krok 4.</strong> Obliczamy procent wszystkich uczestników.</p>
      <BlockMath math={`\\frac{${data.tableTennisTotal}}{${data.total}}\\cdot 100\\%=${formatPercent(data.percent)}\\%`} />
      <p>Dzieci z turnieju tenisa stołowego stanowiły <InlineMath math={`${formatPercent(data.percent)}\\%`} /> wszystkich uczestników.</p>
    </div>
  );
}
